import {
    IAuthState,
    ILoggedInUser,
    initialLoggedInUserState,
    initialLoginState,
    initialRegisterState,
    IObtainEntriesAction
} from "./Helper";
import {toastSuccess} from "../../component/shared/toast/Toast";
import i18n from "../../config/i18n";
import {StorageConstants} from "../../util/constants";
import secureLocalStorage from "react-secure-storage";
import {createSlice, PayloadAction} from "@reduxjs/toolkit";
import {SliceNames} from "../constants";
import {
    changeDefaultLanguage,
    getUserDetails,
    logInUser,
    logOut,
    registerUser,
    socialLoginUser,
    updateUser
} from "./ActionCreators";

const initialValue: IAuthState = {
    loggedInUser: secureLocalStorage.getItem(StorageConstants.USER_INFO) ?
        JSON.parse(secureLocalStorage.getItem(StorageConstants.USER_INFO)! as string) :
        initialLoggedInUserState,
    login: initialLoginState,
    register: initialRegisterState,
    updateUserLoading: false,
    changeLocaleLoading: false
}

const authSlice = createSlice({
    name: SliceNames.AUTH,
    initialState: initialValue,
    reducers: {
        loginReset: (state) => {
            state.login = {
                loading: false,
                error: false,
                errorMessage: ""
            }
        },
        registerReset: (state) => {
            state.register = {
                loading: false,
                error: false,
                success: false,
                message: ""
            }
        },
        updateWithRefreshToken: (state, action: PayloadAction<string>) => {
            const updatedUser = {
                ...state.loggedInUser,
                access_token: action.payload
            }
            secureLocalStorage.setItem(StorageConstants.USER_INFO, JSON.stringify(updatedUser))
            state.loggedInUser = updatedUser
        },
        updateUserAfterObtainingEntries: (state, action: PayloadAction<IObtainEntriesAction>) => {
            let updatedUser
            updatedUser = {
                ...state.loggedInUser,
                available_points: state.loggedInUser.available_points - (action.payload.wish.cost * action.payload.qty)
            }
            secureLocalStorage.setItem(StorageConstants.USER_INFO, JSON.stringify(updatedUser))
            state.loggedInUser = updatedUser
        },
        spinTheWheel: (state, action: PayloadAction<{ points_award: number, server_time: string }>) => {
            const updatedUser: ILoggedInUser = {
                ...state.loggedInUser,
                points: state.loggedInUser.points + action.payload.points_award,
                available_points: state.loggedInUser.available_points + action.payload.points_award,
                has_spin_today: action.payload.server_time
            }
            secureLocalStorage.setItem(StorageConstants.USER_INFO, JSON.stringify(updatedUser))
            state.loggedInUser = updatedUser
        }
    },
    extraReducers: builder => {
        builder
            .addCase(
                logInUser.pending, (state) => {
                    state.login = {
                        loading: true,
                        error: false,
                        errorMessage: ""
                    }
                })
            .addCase(
                logInUser.rejected, (state, action: PayloadAction<any>) => {
                    state.login = {
                        loading: false,
                        error: true,
                        errorMessage: action.payload
                    }
                })
            .addCase(
                socialLoginUser.pending, (state) => {
                    state.login = {
                        loading: true,
                        error: false,
                        errorMessage: ""
                    }
                })
            .addCase(
                socialLoginUser.rejected, (state, action: PayloadAction<any>) => {
                    state.login = {
                        loading: false,
                        error: true,
                        errorMessage: action.payload
                    }
                })
            .addCase(
                getUserDetails.fulfilled, (state, action: PayloadAction<ILoggedInUser>) => {
                    secureLocalStorage.setItem(StorageConstants.USER_INFO, JSON.stringify(action.payload))
                    state.loggedInUser = action.payload
                    state.login = {
                        loading: false,
                        error: false,
                        errorMessage: ""
                    }
                })
            .addCase(
                getUserDetails.rejected, (state, action: PayloadAction<any>) => {
                    state.login = {
                        loading: false,
                        error: true,
                        errorMessage: action.payload
                    }
                })
            .addCase(
                registerUser.pending, (state) => {
                    state.register = {
                        loading: true,
                        error: false,
                        success: false,
                        message: ""
                    }
                })
            .addCase(
                registerUser.fulfilled, (state, action: PayloadAction<string>) => {
                    state.register = {
                        loading: false,
                        error: false,
                        success: true,
                        message: action.payload
                    }
                })
            .addCase(
                registerUser.rejected, (state, action: PayloadAction<any>) => {
                    state.register = {
                        loading: false,
                        error: true,
                        success: false,
                        message: action.payload
                    }
                })
            .addCase(
                updateUser.pending, (state) => {
                    state.updateUserLoading = true
                })
            .addCase(
                updateUser.fulfilled, (state, action: PayloadAction<ILoggedInUser>) => {
                    const updatedUser = {
                        ...state.loggedInUser,
                        ...action.payload
                    }
                    secureLocalStorage.setItem(StorageConstants.USER_INFO, JSON.stringify(updatedUser))
                    state.loggedInUser = updatedUser
                    state.updateUserLoading = false
                })
            .addCase(
                updateUser.rejected, (state) => {
                    state.updateUserLoading = false
                })
            .addCase(
                changeDefaultLanguage.pending, (state) => {
                    state.changeLocaleLoading = true
                })
            .addCase(
                changeDefaultLanguage.fulfilled, (state, action: PayloadAction<any>) => {
                    const updatedUser = {
                        ...state.loggedInUser,
                        default_locale: action.payload
                    }
                    secureLocalStorage.setItem(StorageConstants.USER_INFO, JSON.stringify(updatedUser))
                    state.loggedInUser = updatedUser
                    state.changeLocaleLoading = false
                })
            .addCase(
                changeDefaultLanguage.rejected, (state) => {
                    state.changeLocaleLoading = false
                })
            .addCase(
                logOut.fulfilled, (state, action: PayloadAction<boolean>) => {
                    if (state.loggedInUser.access_token) {
                        if (action.payload) {
                            toastSuccess(i18n.t('logout_success'))
                        }
                        secureLocalStorage.removeItem(StorageConstants.USER_INFO)
                        state.loggedInUser = initialLoggedInUserState
                    }
                }
            )
    }
})

export default authSlice.reducer

export const {
    loginReset,
    registerReset,
    updateWithRefreshToken,
    updateUserAfterObtainingEntries,
    spinTheWheel
} = authSlice.actions